import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import Footer from '../components/Footer';
import { APP_DASHBOARD_URL } from '../lib/appUrl';
import Logo from '../components/Logo';
import { API_URL } from '../lib/apiBase';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Sparkles,
  Zap,
  Target,
  TrendingUp,
  DollarSign,
  Bell,
  Search,
  X,
} from 'lucide-react';

const getVisitorId = () => {
  const KEY = 'vibesprint_visitor_id';
  let v = localStorage.getItem(KEY);
  if (!v) {
    v = `vis_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(KEY, v);
  }
  return v;
};

const ARTICLES = [
  {
    id: 'three-lane-framework',
    icon: Target,
    tag: 'Framework',
    readingTime: '4 min read',
    title: 'The 3-Lane Framework: Build / Fix / Ignore',
    dek:
      "Why every piece of feedback belongs in one of three lanes — and why the Ignore lane is the one that separates good founders from great ones.",
    body: [
      "Most founders drown in feedback because they treat every request as a signal. The 3-Lane Framework flips this: only two lanes deserve action — Build Next (revenue) and Fix Now (churn). Everything else goes into Ignore.",
      "Build Next answers 'what unlocks the next MRR jump?' — feature requests from paying users, integrations that gate deals, product gaps mentioned by trial-to-paid drop-offs. High-signal, high-effort.",
      "Fix Now answers 'what's actively costing me money right now?' — churn triggers, showstopper bugs, complaints from your top revenue segment. Small effort, huge downside if ignored.",
      "Ignore is the discipline. Aesthetic tweaks from free-tier users. Feature requests that don't map to willingness-to-pay. 'Would be nice' asks. Kill them ruthlessly — every hour spent in Ignore is an hour stolen from Build.",
    ],
  },
  {
    id: 'indie-pricing-psychology',
    icon: DollarSign,
    tag: 'Pricing',
    readingTime: '5 min read',
    title: 'Indie SaaS Pricing: The $9 / $24 / $59 Playbook',
    dek:
      'Why three tiers beat five, why $19 loses to $24, and how to structure limits so upgrades feel obvious instead of coerced.',
    body: [
      "Solo SaaS pricing lives or dies on the middle tier. Anchor with a Mini ($9) that's cheap enough to instant-swipe, cap the Pro at what an agency will pay ($59), and let the Basic ($24) do 70% of your revenue.",
      "Round to psychology, not decimals. $24 outperforms $19 for indie SaaS because it signals 'serious tool' without triggering budget approval. $9 outperforms $7 because it filters out perpetual freeloaders.",
      "Meter usage, not seats. Solopreneurs don't have seats. They have jobs to be done. Cap by AI analyses, imports, exports — anything that maps to the value moment. Never cap by 'users.'",
      "Annual = 20% off, no more. Any deeper discount trains customers to expect it. Show both monthly and annual side-by-side with the savings in the badge — never bury the toggle.",
    ],
  },
  {
    id: 'outcome-loop',
    icon: TrendingUp,
    tag: 'Positioning',
    readingTime: '6 min read',
    title: 'The Outcome Loop: Why Feature Backlogs Lose to Revenue Receipts',
    dek:
      "Canny and UserJot ship feature lists. Winners ship revenue lifts. Here's the 30-day post-ship loop that turns your changelog into a moat.",
    body: [
      "A feature backlog is a to-do list. A revenue receipt is a case study. Every shipped feature should have a 30-day follow-up: MRR before, MRR after, and a note on which segment moved.",
      "This is your defensible moat against every 'add a public roadmap' clone: they prove they listen; you prove you deliver.",
      "Instrument the loop early. Tag decisions in your product OS with revenue impact estimates before you build, then reconcile post-ship. Miss the target? That's a signal about your effort estimates. Hit it? That's a testimonial.",
      "Publish the receipts. A quarterly 'What we shipped and what it earned' post beats every marketing landing page — because it's the one thing your competitors can't fake.",
    ],
  },
  {
    id: 'signal-sources',
    icon: Zap,
    tag: 'Ops',
    readingTime: '3 min read',
    title: 'Signal Sources: Where Real Feedback Actually Lives',
    dek:
      "Twitter is dead. Reddit and niche Discords are alive. Here's the exact source list solo founders use to catch churn 30 days before it shows up in Stripe.",
    body: [
      "Stop asking 'where do I get feedback' and start asking 'where does my ICP complain loudly.' For indie SaaS, that's Reddit (r/SaaS, r/indiehackers, subreddit of your niche), Hacker News (Show HN reactions), and 3–4 niche Discord servers.",
      "Automate the pull. Manually reading Reddit is a productivity trap. Wire the RSS feed (every subreddit has one — append `.rss` to the URL) into whatever prioritization tool you use and let signals come to you.",
      "Weight by segment. A complaint from a paying customer is 10× the signal of a random Twitter DM. A trialist mentioning a competitor is 5× a free user asking for a feature. Encode this weighting somewhere.",
      "Set a churn tripwire. If more than two paying customers mention the same friction in a week, that's not feedback — that's a countdown timer.",
    ],
  },
  {
    id: 'kill-your-backlog',
    icon: Sparkles,
    tag: 'Discipline',
    readingTime: '4 min read',
    title: 'Kill Your Feature Backlog: A Founder Playbook for the Ignore Lane',
    dek:
      "The bravest thing you can do this quarter is delete 80% of your backlog. Here's how to Marie-Kondo your roadmap without alienating users.",
    body: [
      "A 200-item Trello backlog is a monument to your inability to say no. Every open card is a debt: it takes cognitive space, it seeds guilt, and it slows every prioritization decision.",
      "Set a 'past-90-days rule.' Any item that hasn't been touched, upvoted, or referenced in the last 90 days gets nuked with prejudice. If it matters, someone will resurface it. If not, it was noise.",
      "Communicate the kill publicly. Update your public roadmap with a monthly 'What we're not building' post. Users respect a founder who says no more than one who ghosts them.",
      "Replace the backlog with three questions: (1) What's the next $1k MRR? (2) What's the next customer save? (3) What's the smallest possible way to test both? Everything else — into the Ignore lane it goes.",
    ],
  },
];

const PlaybookPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(
    !!localStorage.getItem('vibesprint_wl_founders_playbook'),
  );
  const [query, setQuery] = useState('');

  const filteredArticles = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ARTICLES;
    return ARTICLES.filter((a) => {
      const haystack = [
        a.title,
        a.dek,
        a.tag,
        ...(a.body || []),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubscribing(true);
    try {
      await axios.post(`${API_URL}/api/waitlist`, {
        feature: 'founders_playbook',
        visitor_id: getVisitorId(),
        email: email.trim(),
        source: 'playbook_page',
      });
      localStorage.setItem('vibesprint_wl_founders_playbook', 'true');
      setSubscribed(true);
      toast.success('Subscribed', {
        description: "We'll email you the next Founder's Playbook drop.",
      });
    } catch (_e) {
      toast.error('Could not subscribe right now — please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <div className="border-b bg-card sticky top-0 z-40">
        <div className="container-wide mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              data-testid="playbook-back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Home
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <a href="/" className="flex items-center gap-2">
              <Logo size="md" />
            </a>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">Playbook</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/pricing')}
              data-testid="playbook-pricing-link"
            >
              Pricing
            </Button>
            <Button
              asChild
              size="sm"
              className="btn-cta"
              data-testid="playbook-try-app"
            >
              <a href={APP_DASHBOARD_URL}>Try VibeSprint</a>
            </Button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section
        className="section border-b bg-gradient-to-b from-primary/5 via-background to-background"
        data-testid="playbook-hero"
      >
        <div className="container mx-auto max-w-3xl px-4 text-center">
          <Badge variant="outline" className="mb-4 border-primary/40 text-primary">
            <BookOpen className="h-3 w-3 mr-1" />
            Founder&apos;s Playbook
          </Badge>
          <h1 className="heading-xl mb-4">Ship-smart tactics from founders in the arena</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Short, opinionated teardowns on prioritization, pricing, positioning and the small
            operational rituals that separate a $500/mo side-project from a $10k/mo one.
          </p>

          {/* Newsletter opt-in */}
          <form
            onSubmit={handleSubscribe}
            className="mt-8 flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
            data-testid="playbook-subscribe-form"
          >
            <Input
              type="email"
              placeholder="you@founder.email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={subscribed || subscribing}
              className="flex-1"
              data-testid="playbook-email-input"
            />
            <Button
              type="submit"
              disabled={subscribed || subscribing}
              className="btn-cta"
              data-testid="playbook-subscribe-button"
            >
              {subscribed ? (
                <>
                  <Bell className="h-4 w-4 mr-1.5" />
                  Subscribed
                </>
              ) : subscribing ? (
                'Subscribing…'
              ) : (
                <>
                  Get new drops
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </>
              )}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-2">
            One issue every other week. Unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* Articles */}
      <section className="section" data-testid="playbook-articles">
        <div className="container mx-auto max-w-3xl px-4">
          {/* In-page search */}
          <div className="mb-6 sm:mb-8" data-testid="playbook-search-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the playbook — try 'pricing' or 'churn'"
                aria-label="Search playbook articles"
                className="pl-9 pr-10"
                data-testid="playbook-search-input"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  aria-label="Clear search"
                  data-testid="playbook-search-clear"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {query && (
              <p
                className="mt-2 text-xs text-muted-foreground"
                data-testid="playbook-search-result-count"
              >
                {filteredArticles.length === 0
                  ? 'No matches — try a shorter search or clear it.'
                  : `${filteredArticles.length} match${filteredArticles.length === 1 ? '' : 'es'} for “${query}”`}
              </p>
            )}
          </div>

          {filteredArticles.length === 0 ? (
            <Card
              className="p-8 text-center text-sm text-muted-foreground"
              data-testid="playbook-empty"
            >
              <Search className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
              Nothing matched “{query}”. Try “pricing”, “churn”, “Reddit”, or clear the search.
            </Card>
          ) : (
            <div className="grid gap-6">
              {filteredArticles.map((article) => {
                const Icon = article.icon;
                return (
                  <Card
                    key={article.id}
                    id={article.id}
                    className="p-6 sm:p-8 card-hover"
                    data-testid={`playbook-article-${article.id}`}
                  >
                    <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
                      <div className="rounded-md bg-primary/10 p-1.5">
                        <Icon className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <Badge variant="outline" className="text-[10px]">{article.tag}</Badge>
                      <span>·</span>
                      <span>{article.readingTime}</span>
                    </div>
                    <h2
                      className="text-xl sm:text-2xl font-semibold tracking-tight mb-2"
                      data-testid={`playbook-article-title-${article.id}`}
                    >
                      {article.title}
                    </h2>
                    <p className="text-sm sm:text-base text-muted-foreground italic mb-5">
                      {article.dek}
                    </p>
                    <div className="space-y-4 text-sm sm:text-base leading-relaxed">
                      {article.body.map((para, i) => (
                        <p key={i}>{para}</p>
                      ))}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {/* CTA card */}
          <Card className="mt-10 p-6 sm:p-8 text-center border-primary/30 bg-gradient-to-br from-primary/5 via-background to-background">
            <Sparkles className="h-6 w-6 text-primary mx-auto mb-3" />
            <h3 className="text-xl font-semibold mb-2">Stop reading. Start shipping.</h3>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto mb-5">
              Every playbook above becomes real when you run your feedback through VibeSprint&apos;s
              Build / Fix / Ignore lanes with revenue-weighted ROI.
            </p>
            <Button
              asChild
              className="btn-cta"
              data-testid="playbook-cta-app"
            >
              <a href={APP_DASHBOARD_URL}>
                Open the Dashboard
                <ArrowRight className="h-4 w-4 ml-2" />
              </a>
            </Button>
          </Card>
        </div>
      </section>

      <Footer variant="full" />
    </div>
  );
};

export default PlaybookPage;
